import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button, Input, Label, Badge } from '../ui';
import { HiCheckCircle, HiXCircle, HiExternalLink, HiRefresh, HiTrash } from '../icons';
import { tenantService } from '../../services/tenant.service';
import { showToast } from '../../utils/toast';

interface ClioConfigModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}

interface ClioFormData {
  clientId: string;
  clientSecret: string;
}

export function ClioConfigModal({ open, onClose, tenantId, tenantName }: ClioConfigModalProps) {
  const [formData, setFormData] = useState<ClioFormData>({
    clientId: '',
    clientSecret: '',
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [errors, setErrors] = useState<Partial<ClioFormData>>({});
  const queryClient = useQueryClient();

  // Get current tenant details with real Clio connection status
  const {
    data: tenantDetail,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => tenantService.getTenantDetail(tenantId),
    enabled: open,
    staleTime: 30000,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        clientId: tenantDetail?.clioConfig.clientId || '',
        clientSecret: '',
      });
      setErrors({});
      setIsConnecting(false);
    }
  }, [open, tenantDetail]);

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: () => tenantService.testClioConnection(tenantId),
    onSuccess: data => {
      showToast.success(data.message || 'Connection test successful');

      if (data.userInfo) {
        let displayText = `Connected as: ${data.userInfo.name}`;

        if (data.userInfo.email) {
          displayText += ` (${data.userInfo.email})`;
        }

        if (data.userInfo.accountName) {
          displayText += ` - ${data.userInfo.accountName}`;
        }

        showToast.info(displayText);
      }
    },
    onError: error => {
      showToast.error(error instanceof Error ? error.message : 'Connection test failed');
    },
  });

  // Refresh token mutation
  const refreshTokenMutation = useMutation({
    mutationFn: () => tenantService.refreshClioToken(tenantId),
    onSuccess: data => {
      showToast.success(data.message || 'Token refreshed successfully');
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    onError: error => {
      showToast.error(error instanceof Error ? error.message : 'Failed to refresh token');
    },
  });

  // Revoke connection mutation
  const revokeConnectionMutation = useMutation({
    mutationFn: () => tenantService.revokeClioConnection(tenantId),
    onSuccess: data => {
      showToast.success(data.message || 'Connection revoked successfully');
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    onError: error => {
      showToast.error(error instanceof Error ? error.message : 'Failed to revoke connection');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<ClioFormData> = {};

    if (!formData.clientId.trim()) {
      newErrors.clientId = 'Client ID is required';
    }

    // Only require Client Secret if not already connected or if user wants to update it
    const isConnected = tenantDetail?.clioConfig.status === 'connected';
    if (!formData.clientSecret.trim() && !isConnected) {
      newErrors.clientSecret = 'Client Secret is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConnect = async () => {
    if (!validateForm()) return;

    // If already connected and no new client secret provided, ask user to provide it for reconnection
    const isConnected = tenantDetail?.clioConfig.status === 'connected';
    if (isConnected && !formData.clientSecret.trim()) {
      showToast.error('Please enter your Client Secret to reconnect to Clio');
      return;
    }

    setIsConnecting(true);
    try {
      // Generate Clio OAuth URL
      const { authUrl, state } = await tenantService.generateClioAuthUrl(
        tenantId,
        formData.clientId,
        formData.clientSecret
      );

      // Store client credentials temporarily in sessionStorage for callback
      const configData = {
        tenantId,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        state,
      };

      sessionStorage.setItem('clio-config', JSON.stringify(configData));

      // Open Clio authorization in new window
      const authWindow = window.open(
        authUrl,
        'clio-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!authWindow) {
        throw new Error('Failed to open authorization window. Please allow popups for this site.');
      }

      // Listen for the authorization callback
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'CLIO_AUTH_SUCCESS') {
          const { state: receivedState, tokenInfo } = event.data;

          try {
            // Verify state matches
            if (receivedState !== state) {
              throw new Error('Invalid authorization state');
            }

            // ClioCallback already handled the token exchange, just show success
            showToast.success('Clio connection established successfully!');
            refetchStatus();
            queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['tenants'] }); // Refresh tenant list
            onClose();
          } catch (error) {
            showToast.error(error instanceof Error ? error.message : 'Authorization failed');
          } finally {
            sessionStorage.removeItem('clio-config');
            authWindow.close();
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
          }
        } else if (event.data.type === 'CLIO_AUTH_ERROR') {
          showToast.error(event.data.error || 'Authorization failed');
          authWindow.close();
          window.removeEventListener('message', handleMessage);
          setIsConnecting(false);
          sessionStorage.removeItem('clio-config');
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if window is closed manually
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setIsConnecting(false);
          sessionStorage.removeItem('clio-config');
        }
      }, 1000);
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : 'Failed to start authorization');
      setIsConnecting(false);
    }
  };

  const handleInputChange = (field: keyof ClioFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const getStatusBadge = () => {
    if (!tenantDetail) return null;

    switch (tenantDetail.clioConfig.status) {
      case 'connected':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <HiCheckCircle className="h-3 w-3" />
            Connected
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <HiXCircle className="h-3 w-3" />
            Expired
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <HiXCircle className="h-3 w-3" />
            Not Connected
          </Badge>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader>
        <div>
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
            Clio Integration
          </h2>
          <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light mt-1">
            Configure OAuth2 connection for {tenantName}
          </p>
          <div className="mt-3">{getStatusBadge()}</div>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-6">
        {statusLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded w-3/4"></div>
            <div className="h-10 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded"></div>
            <div className="h-10 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded"></div>
          </div>
        ) : (
          <>
            {/* Current Status */}
            {tenantDetail && tenantDetail.clioConfig.status === 'connected' && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                      Connection Active
                    </h3>
                    <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                      {tenantDetail.clioConfig.clientId && (
                        <p>Client ID: {tenantDetail.clioConfig.clientId}</p>
                      )}
                      {tenantDetail.clioConfig.lastConnected && (
                        <p>Last connected: {formatDate(tenantDetail.clioConfig.lastConnected)}</p>
                      )}
                      {tenantDetail.clioConfig.tokenExpiresAt && (
                        <p>Token expires: {formatDate(tenantDetail.clioConfig.tokenExpiresAt)}</p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-green-200 dark:border-green-800">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => testConnectionMutation.mutate()}
                      loading={testConnectionMutation.isPending}
                      disabled={tenantDetail.clioConfig.status !== 'connected'}
                    >
                      Test Connection
                    </Button>
                    {tenantDetail.clioConfig.hasRefreshToken && (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<HiRefresh />}
                        onClick={() => refreshTokenMutation.mutate()}
                        loading={refreshTokenMutation.isPending}
                      >
                        Refresh Token
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon={<HiTrash />}
                      onClick={() => revokeConnectionMutation.mutate()}
                      loading={revokeConnectionMutation.isPending}
                    >
                      Revoke Connection
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Configuration Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="clientId">
                  Clio Client ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="clientId"
                  type="text"
                  placeholder="Enter your Clio Client ID"
                  value={formData.clientId}
                  onChange={e => handleInputChange('clientId', e.target.value)}
                  error={errors.clientId}
                  disabled={isConnecting}
                  className="w-full font-mono text-sm"
                />
                <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-light mt-1">
                  Found in your Clio App settings under API Credentials
                </p>
              </div>

              <div>
                <Label htmlFor="clientSecret">
                  Clio Client Secret <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="clientSecret"
                  type="password"
                  placeholder={
                    tenantDetail?.clioConfig.status === 'connected'
                      ? '••••••••••••••••••••••••••••••••••••••••'
                      : 'Enter your Clio Client Secret'
                  }
                  value={formData.clientSecret}
                  onChange={e => handleInputChange('clientSecret', e.target.value)}
                  error={errors.clientSecret}
                  disabled={isConnecting}
                  className="w-full font-mono text-sm"
                />
                <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-light mt-1">
                  {tenantDetail?.clioConfig.status === 'connected'
                    ? 'Client Secret is securely stored. Leave empty to keep current secret, or enter new one to update.'
                    : 'Keep this secret secure and never share it'}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-2">
                Setup Instructions:
              </h4>
              <ol className="text-sm text-xtnd-dark-600 dark:text-xtnd-light space-y-1 list-decimal list-inside">
                <li>Log in to your Clio account and navigate to App Settings</li>
                <li>Create a new API application or use existing credentials</li>
                <li>
                  Set the redirect URI to:{' '}
                  <code className="bg-xtnd-dark-200 dark:bg-xtnd-dark-700 px-1 rounded text-xs">
                    {window.location.origin}/auth/clio/callback
                  </code>
                </li>
                <li>Copy the Client ID and Client Secret to the fields above</li>
                <li>Click "Connect to Clio" to authorize the integration</li>
              </ol>
            </div>
          </>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isConnecting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={!formData.clientId || !formData.clientSecret || statusLoading}
          leftIcon={<HiExternalLink />}
        >
          {tenantDetail?.clioConfig.status === 'connected'
            ? 'Reconnect to Clio'
            : 'Connect to Clio'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
